import { Link } from 'react-router';
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import {
  applyConsentSelection,
  initializeAnalyticsFromConsent,
  readGdprConsent,
} from '../lib/gdpr-consent';

interface HeaderProps {
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
}

interface MeResponse {
  user: {
    id: number;
    nume: string;
    prenume: string;
    email: string;
  } | null;
}

export function Header({ theme, onToggleTheme }: HeaderProps) {
  const queryClient = useQueryClient();

  useEffect(() => {
    initializeAnalyticsFromConsent(readGdprConsent());

    const onConsentUpdated = () => {
      const consent = readGdprConsent();
      if (consent) {
        applyConsentSelection(consent);
      }
    };

    window.addEventListener('gdpr-consent-updated', onConsentUpdated);
    return () => window.removeEventListener('gdpr-consent-updated', onConsentUpdated);
  }, []);

  const { data: meData } = useQuery<MeResponse>({
    queryKey: ['front-auth', 'me'],
    queryFn: async () => (await apiClient.get('/auth/me')).data,
    retry: false,
  });

  const logoutMutation = useMutation({
    mutationFn: async () => (await apiClient.post('/auth/logout')).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['front-auth', 'me'] });
      await queryClient.invalidateQueries({ queryKey: ['front-auth', 'dashboard'] });
      queryClient.setQueryData(['front-auth', 'me'], { user: null });
      queryClient.setQueryData(['front-auth', 'dashboard'], undefined);
    },
  });

  const user = meData?.user ?? null;

  return (
    <header className="border-b px-6 py-4 site-header">
      <div className="mx-auto flex max-w-7xl items-center justify-between">
        <Link to="/" className="text-xl font-bold site-brand">
          Rezervă Autocar
        </Link>

        <nav className="flex items-center gap-4">
          <Link to="/cont" className="site-nav-link">
            {user ? `Cont: ${user.nume} ${user.prenume}` : 'Cont / login'}
          </Link>

          {user && (
            <button
              type="button"
              className="site-nav-link"
              onClick={() => logoutMutation.mutate()}
              disabled={logoutMutation.isPending}
            >
              {logoutMutation.isPending ? 'Se deconectează...' : 'Logout'}
            </button>
          )}

          <button
            type="button"
            onClick={onToggleTheme}
            className="rounded border px-3 py-1.5 text-sm site-theme-toggle"
            title={theme === 'dark' ? 'Comută pe tema light' : 'Comută pe tema dark'}
          >
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
        </nav>
      </div>
    </header>
    
  );
}